import { resolveMarket, type Market, type MarketId } from './markets';

const ONBOARDING_BASE = (process.env.REACT_APP_ONBOARDING_URL || 'https://onboard.comtev.com').replace(/\/$/, '');

// The market this bundle was built for, selected by REACT_APP_MARKET at build
// time (global = comtev.com, ng = comtev.ng). `resolveMarket` falls back to the
// default rather than throwing, because a module-init throw here would render a
// white screen; an unrecognised value is rejected by scripts/resolve-market.js
// before webpack even starts, so the fallback is unreachable in a real build.
export const market: Market = resolveMarket(process.env.REACT_APP_MARKET);
export const marketId: MarketId = market.id;

// Absolute origin this build is served from. The single source for canonical
// URLs, OG tags and JSON-LD — see usePageMeta.
export const SITE_URL: string = market.siteUrl;

export const config = {
    apiUrl: (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, ''),
    googleClientId: process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
    onboardingUrl: ONBOARDING_BASE,
    onboardingVenueUrl: `${ONBOARDING_BASE}/venue`,
    onboardingVendorUrl: `${ONBOARDING_BASE}/vendor`,
    sentryDsn: process.env.REACT_APP_SENTRY_DSN || '',
    environment: process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development',
    release: process.env.REACT_APP_RELEASE || undefined,
};

// This is the MARKETING site (comtev.com). The authenticated app lives on a
// separate origin (app.comtev.com in prod; the local app dev server in dev),
// so every app entry point (login) and every app/token deep link must be an
// absolute cross-origin URL. `appOrigin` is the app's origin whenever we are
// NOT already on it; empty only in the degenerate case where the marketing
// build is somehow served from the app origin (then links stay same-origin).
//
// REACT_APP_APP_URL lets local dev point marketing at the local app dev server
// (e.g. http://localhost:3000 or https://demo.comtev.com); it defaults to the
// prod app host. Origin comparison (not hostname) so localhost:3001 vs
// localhost:3000 is correctly treated as cross-origin.
const APP_URL = (process.env.REACT_APP_APP_URL || 'https://app.comtev.com').replace(/\/$/, '');
export const appOrigin =
    typeof window !== 'undefined' && window.location.origin !== APP_URL ? APP_URL : '';
export const appLoginUrl = (): string => `${appOrigin}/login`;

// Straight to the Create Account form. The app reads `?signup=1` (see
// comtev-frontend LoginPage) rather than router state, because the marketing
// sites cross origins with window.location.replace, which throws state away.
// That is why this is a query string and not a path.
// The `src` marker tells the app which funnel the visitor came from: on
// comtev.ng it locks the new account to EVENT_PLANNER and requires a work
// email (see the api's app.signup_policy). Global builds carry no marker.
export const appSignupUrl = (): string =>
    market.signupSource
        ? `${appOrigin}/login?signup=1&src=${market.signupSource}`
        : `${appOrigin}/login?signup=1`;

// App/token path prefixes that do NOT live on the marketing site. Hitting one
// on comtev.com (stale bookmark, emailed link before the edge 301 is live) is
// bounced to the app origin by the router catch-all (see App.tsx). Keep in sync
// with the Traefik redirectregex in comtev-deploy stack.j2 (Phase 3).
export const APP_PATH_PREFIXES = [
    '/login', '/forgot-password', '/reset-password', '/activate', '/accept-invite',
    '/onboarding', '/dashboard', '/occasion', '/account', '/earnings', '/suppliers',
    '/billing', '/venues', '/notifications', '/admin', '/payouts', '/moodboards',
    '/gather', '/invites',
    // Public token links minted by the API — moved to the app host.
    '/l/', '/client-invoices', '/proposal', '/comparison', '/collaborate',
    '/integrations/slack/link',
];

// Validate required environment variables in development
if (process.env.NODE_ENV === 'development') {
    if (!process.env.REACT_APP_API_URL) {
        console.warn('⚠️ REACT_APP_API_URL is not set, using default: http://localhost:8000');
    }
    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
        console.warn('⚠️ REACT_APP_GOOGLE_CLIENT_ID is not set');
    }
}